import React, { useEffect, useState, Fragment } from 'react';
import { apiRequest } from '../utils/api';
import { toast } from 'react-toastify';
import { Switch, Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { useTheme } from '../contexts/ThemeContext';

const defaultPrefs = {
  email_updates: true,
  analysis_complete: true,
  security_alerts: true,
  marketing: true,
  theme: 'light',
  language: 'English',
  timezone: 'UTC',
  auto_save: true,
};

const themeOptions = ['light', 'dark'];
const languageOptions = ['English', 'French', 'Spanish'];
const timezoneOptions = ['UTC', 'GMT', 'EST', 'CST', 'PST'];

// Custom confirmation modal
const ConfirmModal = ({ open, onCancel, onConfirm }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl p-8 max-w-md w-full shadow-2xl">
        <h2 className="text-2xl font-bold text-red-400 mb-4">Delete Account Permanently</h2>
        <p className="text-[var(--color-text-secondary)] mb-6">Are you sure you want to <span className="text-red-400 font-semibold">delete your account permanently</span>? This action <span className="font-semibold">cannot be undone</span>. We will remove account data and associated files we identify as yours; payment and security records may be retained where required by law or billing rules.</p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-6 py-2 rounded-lg bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] font-medium hover:bg-gray-600 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-6 py-2 rounded-lg bg-red-700 text-[var(--color-text)] font-bold hover:bg-red-800 transition"
          >
            Delete Permanently
          </button>
        </div>
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const { theme, setTheme } = useTheme();
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const data = await apiRequest('users/preferences');
        setPrefs({ ...defaultPrefs, ...data, theme });
      } catch {
        toast.error('Failed to load preferences');
      } finally {
        setLoading(false);
      }
    };
    fetchPrefs();
  }, []);

  useEffect(() => {
    setPrefs((p) => (p.theme === theme ? p : { ...p, theme }));
  }, [theme]);

  const handleToggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));
  const handleChange = (key, value) => setPrefs((p) => ({ ...p, [key]: value }));

  const handleThemeChange = (value) => {
    setTheme(value);
    setPrefs((p) => ({ ...p, theme: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiRequest('users/preferences', { method: 'PUT', body: prefs });
      toast.success('Settings saved!');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      toast.info('Preparing your export...');
      // Only POST to backend, no download logic
      await apiRequest('users/export-data', { method: 'POST' });
      toast.success('Your data export will be sent to your email shortly.');
    } catch (err) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiRequest('users/account-permanent', { method: 'DELETE' });
      toast.success('Account deleted.');
      window.location.href = '/';
    } catch {
      toast.error('Failed to delete account');
    }
  };

  if (loading) return <div className="p-8 text-center text-[var(--color-text-muted)]">Loading settings...</div>;

  return (
    <div className="max-w-7xl mx-auto p-6 w-full">
      <div className="mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)] mb-2 leading-tight">
          Account <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">Settings</span>
        </h1>
        <p className="text-lg text-[var(--color-text-muted)]">Manage your preferences and account settings.</p>
      </div>
      {/* Notifications */}
      <div className="w-full elevated-card border border-[var(--color-border)] rounded-2xl p-10 mb-12">
        <h2 className="text-2xl font-bold text-[var(--color-text)] mb-6">Notifications</h2>
        <div className="space-y-6">
          {[
            { key: 'email_updates', label: 'Email Updates', desc: 'Receive email notifications about platform updates' },
            { key: 'analysis_complete', label: 'Analysis Complete', desc: 'Get notified when document analysis is complete' },
            { key: 'security_alerts', label: 'Security Alerts', desc: 'Receive alerts about account security events' },
            { key: 'marketing', label: 'Marketing', desc: 'Receive promotional emails and offers' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-4 px-6 bg-[var(--color-surface-muted)]/50 rounded-xl">
              <div>
                <div className="font-semibold text-[var(--color-text)]">{label}</div>
                <div className="text-[var(--color-text-muted)] text-sm">{desc}</div>
              </div>
              <Switch
                checked={!!prefs[key]}
                onChange={() => handleToggle(key)}
                className={`${prefs[key] ? 'bg-[var(--color-primary)]' : 'bg-gray-600'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
              >
                <span className="sr-only">Toggle {label}</span>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${prefs[key] ? 'translate-x-6' : 'translate-x-1'}`} />
              </Switch>
            </div>
          ))}
        </div>
      </div>
      {/* Appearance */}
      <div className="w-full elevated-card border border-[var(--color-border)] rounded-2xl p-10 mb-12">
        <h2 className="text-2xl font-bold text-[var(--color-text)] mb-6">Appearance</h2>
        <div className="flex items-center justify-between py-4 px-6 bg-[var(--color-surface-muted)]/50 rounded-xl">
          <div>
            <div className="font-semibold text-[var(--color-text)]">Theme</div>
            <div className="text-[var(--color-text-muted)] text-sm">Choose light or dark mode for the app</div>
          </div>
          <div className="flex gap-2">
            {themeOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleThemeChange(option)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${
                  theme === option
                    ? 'bg-[var(--color-primary)] text-[var(--color-text)]'
                    : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] hover:bg-gray-600'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Preferences section is commented out for now. Restore in the future if needed. */}
      {/* Data Management */}
      <div className="w-full elevated-card border border-[var(--color-border)] rounded-2xl p-10 mb-12">
        <h2 className="text-2xl font-bold text-[var(--color-text)] mb-6">Data Management</h2>
        <div className="flex flex-col md:flex-row md:space-x-6 space-y-4 md:space-y-0">
          <button
            onClick={handleExport}
            className="flex-1 px-6 py-4 rounded-lg brand-gradient-btn font-semibold  transition-all flex items-center justify-center mb-2 md:mb-0"
            disabled={exporting}
          >
            {exporting ? (
              <span className="flex items-center"><span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>Exporting...</span>
            ) : (
              <><span className="mr-2">⬇️</span> Export My Data</>
            )}
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex-1 px-6 py-4 rounded-lg bg-red-800/90 text-red-100 font-semibold hover:bg-red-900 transition-all flex items-center justify-center"
          >
            <span className="mr-2">❌</span> Delete Account Permanent
          </button>
        </div>
      </div>
      <ConfirmModal
        open={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          setShowDeleteModal(false);
          await handleDelete();
        }}
      />
      {/* Save Button */}
      <div className="flex justify-center mt-8">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 rounded-xl brand-gradient-btn font-bold text-lg shadow-lg  transition-all disabled:opacity-60"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage; 